<template>
    <div class="structured-json-form">
        <!-- List Mode (Array of Objects) -->
        <div v-if="isList" class="list-mode">
            <div v-for="(item, index) in internalListValue" :key="'item-' + index" class="list-item">
                <div class="item-header">
                    <span>#{{ index + 1 }}</span>
                    <a-button type="danger" size="small" icon="delete" @click="removeItem(index)" />
                </div>
                <div class="item-body">
                    <div v-for="field in schema" :key="field.name" class="form-field mb-2">
                        <div class="field-header">
                            <label :class="{ required: field.required }">{{ field.label }}</label>
                            <a-radio-group 
                                v-model="fieldModes[`${index}_${field.name}`]" 
                                button-style="solid" 
                                size="small"
                                @change="onFieldModeChange(index, field.name)"
                            >
                                <a-radio-button value="static">Statik</a-radio-button>
                                <a-radio-button value="dynamic">Dinamik</a-radio-button>
                            </a-radio-group>
                        </div>
                        
                        <!-- Static Mode -->
                        <template v-if="fieldModes[`${index}_${field.name}`] === 'static'">
                            <!-- Text -->
                            <a-input
                                v-if="field.type === 'text'"
                                :value="item[field.name]"
                                @input="updateItemField(index, field.name, $event.target.value)"
                                :placeholder="field.description || field.label"
                            />
                            
                            <!-- User ID -->
                            <a-select
                                v-else-if="field.type === 'userid'"
                                :value="item[field.name]"
                                @change="updateItemField(index, field.name, $event)"
                                allowClear
                                :placeholder="field.description || 'Select user'"
                                style="width: 100%"
                            >
                                <a-select-option v-for="user in users" :key="user._id" :value="user._id">
                                    {{ user.name }}
                                </a-select-option>
                            </a-select>
                            
                            <!-- Contact ID -->
                            <a-input
                                v-else-if="field.type === 'contactid'"
                                :value="item[field.name]"
                                @input="updateItemField(index, field.name, $event.target.value)"
                                :placeholder="field.description || 'Contact ID (örn: {{id}})'"
                            />
                            
                            <!-- Chat ID -->
                            <a-input
                                v-else-if="field.type === 'chatid'"
                                :value="item[field.name]"
                                @input="updateItemField(index, field.name, $event.target.value)"
                                :placeholder="field.description || 'Chat ID (örn: {{conversationId}})'"
                            />

                            <!-- Number -->
                            <a-input-number
                                v-else-if="field.type === 'number'"
                                :value="item[field.name]"
                                @change="updateItemField(index, field.name, $event)"
                                style="width: 100%"
                            />

                            <!-- Boolean -->
                            <div v-else-if="field.type === 'boolean'">
                                <a-switch 
                                    :checked="item[field.name]"
                                    @change="updateItemField(index, field.name, $event)"
                                />
                                <span class="ml-2">{{ item[field.name] ? 'True' : 'False' }}</span>
                            </div>

                            <!-- Array (Primitive) -->
                            <a-select
                                v-else-if="field.type === 'array'"
                                :value="item[field.name]"
                                @change="updateItemField(index, field.name, $event)"
                                mode="tags"
                                style="width: 100%"
                                :placeholder="field.description || 'Add items'"
                            />
                        </template>
                        
                        <!-- Dynamic Mode -->
                        <template v-else>
                            <TiptapEditor
                                :value="item[field.name]"
                                @input="updateItemField(index, field.name, $event)"
                                :placeholder="field.description || '{{variable}}'"
                                :rows="1"
                            />
                        </template>
                    </div>
                </div>
            </div>
            <a-button type="dashed" block icon="plus" @click="addItem">
                {{ $t('message.add') || 'Add Item' }}
            </a-button>
        </div>

        <!-- Object Mode (Single Object) -->
        <div v-else class="object-mode">
            <div v-for="field in schema" :key="field.name" class="form-field mb-3">
                <div class="field-header mb-2">
                    <label :class="{ required: field.required }" class="d-block" style="font-weight: 500">{{ field.label }}</label>
                    <a-radio-group 
                        v-model="fieldModes[field.name]" 
                        button-style="solid" 
                        size="small"
                        @change="onFieldModeChange(null, field.name)"
                    >
                        <a-radio-button value="static">Statik</a-radio-button>
                        <a-radio-button value="dynamic">Dinamik</a-radio-button>
                    </a-radio-group>
                </div>

                <!-- Static Mode -->
                <template v-if="fieldModes[field.name] === 'static'">
                    <!-- Text -->
                    <a-input
                        v-if="field.type === 'text'"
                        :value="internalObjectValue[field.name]"
                        @input="updateObjectField(field.name, $event.target.value)"
                        :placeholder="field.description || field.label"
                    />
                    
                    <!-- User ID -->
                    <a-select
                        v-else-if="field.type === 'userid'"
                        :value="internalObjectValue[field.name]"
                        @change="updateObjectField(field.name, $event)"
                        allowClear
                        :placeholder="field.description || 'Select user'"
                        style="width: 100%"
                    >
                        <a-select-option v-for="user in users" :key="user._id" :value="user._id">
                            {{ user.name }}
                        </a-select-option>
                    </a-select>
                    
                    <!-- Contact ID -->
                    <a-input
                        v-else-if="field.type === 'contactid'"
                        :value="internalObjectValue[field.name]"
                        @input="updateObjectField(field.name, $event.target.value)"
                        :placeholder="field.description || 'Contact ID (örn: {{id}})'"
                    />
                    
                    <!-- Chat ID -->
                    <a-input
                        v-else-if="field.type === 'chatid'"
                        :value="internalObjectValue[field.name]"
                        @input="updateObjectField(field.name, $event.target.value)"
                        :placeholder="field.description || 'Chat ID (örn: {{conversationId}})'"
                    />

                    <!-- Number -->
                    <a-input-number
                        v-else-if="field.type === 'number'"
                        :value="internalObjectValue[field.name]"
                        @change="updateObjectField(field.name, $event)"
                        style="width: 100%"
                    />

                    <!-- Boolean -->
                    <div v-else-if="field.type === 'boolean'">
                        <a-switch 
                            :checked="internalObjectValue[field.name]"
                            @change="updateObjectField(field.name, $event)"
                        />
                        <span class="ml-2">{{ internalObjectValue[field.name] ? 'True' : 'False' }}</span>
                    </div>

                    <!-- Array (Primitive) -->
                    <a-select
                        v-else-if="field.type === 'array'"
                        :value="internalObjectValue[field.name]"
                        @change="updateObjectField(field.name, $event)"
                        mode="tags"
                        style="width: 100%"
                        :placeholder="field.description || 'Add items'"
                    />
                </template>
                
                <!-- Dynamic Mode -->
                <template v-else>
                    <TiptapEditor
                        :value="internalObjectValue[field.name]"
                        @input="updateObjectField(field.name, $event)"
                        :placeholder="field.description || '{{variable}}'"
                        :rows="1"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import TiptapEditor from "../common/TiptapEditor.vue";
import { mapState } from "vuex";

export default {
    name: "StructuredJsonForm",
    components: {
        TiptapEditor,
    },
    props: {
        value: {
            type: [Object, Array],
            default: () => ({}),
        },
        schema: {
            type: Array,
            required: true,
        },
        isList: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapState({
            users: (state) => state.user.users?.data || [],
        }),
    },
    data() {
        return {
            internalObjectValue: {},
            internalListValue: [],
            fieldModes: {}, // Track static/dynamic for each field
            initialized: false,
        };
    },
    watch: {
        value: {
            immediate: true,
            handler(val, oldVal) {
                // Sadece ilk yüklemede veya parent'tan gerçekten yeni bir değer geldiğinde güncelle
                // Kendi emit ettiğimiz değişikliklerde yeniden set etme
                if (!this.initialized) {
                    if (this.isList) {
                        this.internalListValue = Array.isArray(val) && val.length > 0 ? JSON.parse(JSON.stringify(val)) : [];
                    } else {
                        this.internalObjectValue = val && Object.keys(val).length > 0 ? JSON.parse(JSON.stringify(val)) : this.getDefaultObjectValue();
                    }
                    this.initializeFieldModes();
                    this.initialized = true;
                }
            },
        },
        schema: {
            handler() {
                // Schema değiştiğinde field modes'u yeniden initialize et
                this.initializeFieldModes();
            },
        },
    },
    methods: {
        getDefaultObjectValue() {
            const obj = {};
            this.schema.forEach((field) => {
                obj[field.name] = this.getDefaultValue(field);
            });
            return obj;
        },
        getDefaultValue(field) {
            if (field.defaultValue !== undefined && field.defaultValue !== null) {
                return field.defaultValue;
            }
            if (field.type === 'boolean') return false;
            if (field.type === 'array') return [];
            if (field.type === 'number') return 0;
            return '';
        },
        initializeFieldModes() {
            const modes = {};
            
            if (this.isList) {
                if (Array.isArray(this.internalListValue)) {
                    this.internalListValue.forEach((item, index) => {
                        if (item && typeof item === 'object') {
                            this.schema.forEach((field) => {
                                const key = `${index}_${field.name}`;
                                const value = item[field.name];
                                modes[key] = this.isDynamicValue(value) ? 'dynamic' : 'static';
                            });
                        }
                    });
                }
            } else {
                if (this.internalObjectValue && typeof this.internalObjectValue === 'object') {
                    this.schema.forEach((field) => {
                        const value = this.internalObjectValue[field.name];
                        modes[field.name] = this.isDynamicValue(value) ? 'dynamic' : 'static';
                    });
                }
            }
            
            this.fieldModes = modes;
        },
        isDynamicValue(value) {
            if (typeof value === 'string' && value.includes('{{') && value.includes('}}')) {
                return true;
            }
            return false;
        },
        updateObjectField(fieldName, value) {
            if (!this.internalObjectValue || typeof this.internalObjectValue !== 'object') {
                this.internalObjectValue = {};
            }
            this.$set(this.internalObjectValue, fieldName, value);
            this.emitValue();
        },
        updateItemField(index, fieldName, value) {
            if (!this.internalListValue[index] || typeof this.internalListValue[index] !== 'object') {
                this.$set(this.internalListValue, index, {});
            }
            this.$set(this.internalListValue[index], fieldName, value);
            this.emitValue();
        },
        onFieldModeChange(index, fieldName) {
            // When switching from dynamic to static, clear the value
            const key = index !== null ? `${index}_${fieldName}` : fieldName;
            const mode = this.fieldModes[key];
            
            if (mode === 'static') {
                // Clear if it has template syntax
                if (index !== null) {
                    const currentValue = this.internalListValue[index][fieldName];
                    if (this.isDynamicValue(currentValue)) {
                        this.updateItemField(index, fieldName, '');
                    }
                } else {
                    const currentValue = this.internalObjectValue[fieldName];
                    if (this.isDynamicValue(currentValue)) {
                        this.updateObjectField(fieldName, '');
                    }
                }
            }
        },
        emitValue() {
            this.$emit("input", this.isList ? this.internalListValue : this.internalObjectValue);
        },
        addItem() {
            const newItem = {};
            this.schema.forEach((field) => {
                newItem[field.name] = this.getDefaultValue(field);
            });
            this.internalListValue.push(newItem);
            
            // Initialize modes for new item
            const index = this.internalListValue.length - 1;
            this.schema.forEach((field) => {
                const key = `${index}_${field.name}`;
                this.$set(this.fieldModes, key, 'static');
            });
            
            this.emitValue();
        },
        removeItem(index) {
            this.internalListValue.splice(index, 1);
            this.emitValue();
        },
    },
    created() {
        // Initialize with default values if empty
        if (this.isList) {
            if (!Array.isArray(this.internalListValue)) {
                this.internalListValue = [];
            }
        } else {
            if (!this.internalObjectValue || typeof this.internalObjectValue !== 'object' || Object.keys(this.internalObjectValue).length === 0) {
                this.internalObjectValue = this.getDefaultObjectValue();
            }
        }
        this.initializeFieldModes();
    },
};
</script>

<style lang="scss" scoped>
.structured-json-form {
    background: #fafafa;
    padding: 12px;
    border-radius: 6px;
    border: 1px solid #e8e8e8;

    .list-item {
        background: #fff;
        border: 1px solid #e8e8e8;
        border-radius: 4px;
        margin-bottom: 12px;
        padding: 12px;

        .item-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            font-weight: 600;
            color: #1890ff;
        }
    }

    .field-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
    }

    .required::after {
        content: "*";
        color: #f5222d;
        margin-left: 4px;
    }
}
</style>

